import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { IngredientListRow } from "@/modules/inventory/domain/types";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
  SortFieldDTO,
} from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useDeleteIngredient, useIngredientsQuery } from "../api/queries";
import { IngredientsBulkCategoryDialog } from "../ui/IngredientsBulkCategoryDialog";
import { IngredientsFilter } from "../ui/IngredientsFilter";
import { IngredientsTable } from "../ui/IngredientsTable";
import { IngredientsToolbar } from "../ui/IngredientsToolbar";

function applyFilters(
  rows: IngredientListRow[],
  filters: FilterConditionDTO[] | undefined,
): IngredientListRow[] {
  if (!filters?.length) return rows;
  return rows.filter((row) => {
    for (const cond of filters) {
      if (cond.op === "or" && Array.isArray(cond.value)) {
        const orMatch = (cond.value as FilterConditionDTO[]).some((c) =>
          matchOne(row, c),
        );
        if (!orMatch) return false;
        continue;
      }
      if (!matchOne(row, cond)) return false;
    }
    return true;
  });
}

function matchOne(row: IngredientListRow, cond: FilterConditionDTO): boolean {
  if (cond.field === "name" && cond.op === "contains") {
    const v = String(cond.value ?? "").toLowerCase();
    return row.name.toLowerCase().includes(v);
  }
  if (cond.field === "isActive" && cond.op === "eq") {
    return row.isActive === cond.value;
  }
  if (cond.field === "categoryId" && cond.op === "eq") {
    const v = cond.value as string;
    if (v === "__uncategorized__") return row.categoryId == null;
    return row.categoryId === v;
  }
  return true;
}

function applySort(
  rows: IngredientListRow[],
  sort: SortFieldDTO[] | undefined,
): IngredientListRow[] {
  if (!sort?.[0]) return [...rows];
  const { field, dir } = sort[0];
  const mult = dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const aVal = (a as Record<string, unknown>)[field];
    const bVal = (b as Record<string, unknown>)[field];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1 * mult;
    if (bVal == null) return -1 * mult;
    if (typeof aVal === "string" && typeof bVal === "string")
      return aVal.localeCompare(bVal) * mult;
    if (typeof aVal === "number" && typeof bVal === "number")
      return (aVal - bVal) * mult;
    return 0;
  });
}

export function IngredientsPage() {
  const nav = useNavigate({ from: "/app/inventory/ingredients" });
  const search = useSearch({
    from: "/app/inventory/ingredients",
  }) as Partial<OffsetPageQueryDTO>;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;
  const sort = search.sort;
  const filters = search.filters;

  const list = useIngredientsQuery();
  const canManage = useActionPermission(["inventory:create"]);
  const canBulkAssign = useActionPermission(["inventory:update"]);
  const deleteIngredient = useDeleteIngredient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const { pageData, totalCount } = useMemo(() => {
    const all = list.data ?? [];
    const filtered = applyFilters(all, filters);
    const sorted = applySort(filtered, sort);
    const total = sorted.length;
    const page = sorted.slice(offset, offset + limit);
    return { pageData: page, totalCount: total };
  }, [list.data, filters, sort, offset, limit]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ວັດຖຸດິບ",
              to: "/app/inventory/ingredients",
            },
          ]}
        />
      </Header>
      <Main>
        <IngredientsToolbar
          canManage={!!canManage}
          canBulkAssign={!!canBulkAssign}
          selectedCount={selectedIds.size}
          onCreate={() => nav({ to: "/app/inventory/ingredients/create" })}
          onBulkAssign={() => setBulkOpen(true)}
        />

        <IngredientsBulkCategoryDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          ingredientIds={[...selectedIds]}
          onSuccess={() => {
            setSelectedIds(new Set());
            toast.success("ມອບຫມວດສຳເລັດ");
          }}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <IngredientsFilter />

          <IngredientsTable
            data={pageData}
            isLoading={list.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={(newOffset, newLimit) =>
              nav({ search: { ...search, offset: newOffset, limit: newLimit } })
            }
            sortBy={sort?.[0]?.field}
            sortOrder={
              sort?.[0]?.dir === "asc" || sort?.[0]?.dir === "desc"
                ? sort[0].dir
                : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row: IngredientListRow) =>
              nav({
                to: "/app/inventory/ingredients/$id/edit",
                params: { id: row.id },
              })
            }
            onDelete={async (id) =>
              toast.promise(deleteIngredient.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບວັດຖຸດິບສຳເລັດ",
                error: "ລຶບວັດຖຸດິບລົ້ມເຫຼວ",
              })
            }
            selectable={!!canBulkAssign}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
          />
        </div>
      </Main>
    </>
  );
}
